import { useEffect } from 'react'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'

export interface UseSelectedWaypointMarkerProps {
  selectedWaypoint?: any
  centerOnWaypoint?: any
}

/**
 * Hook for managing selected waypoint marker rendering
 * Shows a blue marker for the currently selected waypoint
 * DISABLED: No automatic centering/zooming - users can manually navigate if needed
 */
export function useSelectedWaypointMarker({
  selectedWaypoint,
  centerOnWaypoint
}: UseSelectedWaypointMarkerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()

  // DISABLED: No automatic centering on waypoint selection
  // This prevents annoying automatic zooming when clicking waypoints
  // Users can manually navigate to waypoints if needed

  // Render selected waypoint marker
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) return

    // Clear marker if no selected waypoint or invalid coordinates
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      try {
        if (map.current.getLayer('selected-waypoint-marker')) {
          map.current.removeLayer('selected-waypoint-marker')
        }
        if (map.current.getSource('selected-waypoint-marker')) {
          map.current.removeSource('selected-waypoint-marker')
        }
      } catch (error) {
        // Ignore errors during cleanup (source/layer may not exist)
      }
      return
    }

    // Skip showing selected marker for waypoints that already have their own markers:
    // 1. Library waypoints (folder_id is set) - have custom markers (book/pin icons)
    //    If a waypoint has folder_id, it's in the library and has its own icon, so don't show blue marker
    // 2. Itinerary waypoints (trip_id is set) - have numbered day-based markers
    // 3. GPX imported standalone waypoints (legacy check)
    const hasLibraryMarker = selectedWaypoint.folder_id != null
    const isItineraryWaypoint = selectedWaypoint.trip_id != null
    const isGpxStandalone = selectedWaypoint.source === 'gpx_import' && !selectedWaypoint.trip_id
    
    if (hasLibraryMarker || isItineraryWaypoint || isGpxStandalone) {
      // Clear any existing marker
      try {
        if (map.current.getLayer('selected-waypoint-marker')) {
          map.current.removeLayer('selected-waypoint-marker')
        }
        if (map.current.getSource('selected-waypoint-marker')) {
          map.current.removeSource('selected-waypoint-marker')
        }
      } catch (error) {
      }
      return
    }
    
    // Validate coordinates are numbers
    const lat = Number(selectedWaypoint.latitude)
    const lng = Number(selectedWaypoint.longitude)
    
    if (isNaN(lat) || isNaN(lng)) {
      // Invalid coordinates - clear marker
      try {
        if (map.current.getLayer('selected-waypoint-marker')) {
          map.current.removeLayer('selected-waypoint-marker')
        }
        if (map.current.getSource('selected-waypoint-marker')) {
          map.current.removeSource('selected-waypoint-marker')
        }
      } catch (error) {
      }
      return
    }

    const selectedFeature = {
      type: 'Feature' as const,
      properties: {
        id: 'selected-waypoint',
        waypointId: selectedWaypoint.id
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [lng, lat]
      }
    }

    const geoJsonData = {
      type: 'FeatureCollection' as const,
      features: [selectedFeature]
    }

    if (!map.current?.isStyleLoaded()) return

    try {
      if (map.current.getSource('selected-waypoint-marker')) {
        (map.current.getSource('selected-waypoint-marker') as mapboxgl.GeoJSONSource).setData(geoJsonData)
      } else {
        map.current.addSource('selected-waypoint-marker', {
          type: 'geojson',
          data: geoJsonData
        })

        map.current.addLayer({
          id: 'selected-waypoint-marker',
          type: 'circle',
          source: 'selected-waypoint-marker',
          paint: {
            'circle-color': '#3b82f6',
            'circle-radius': 14,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 4,
            'circle-opacity': 1.0
          }
        })

        // Ensure selected waypoint marker is on top
        const layers = ['waypoint-markers', 'poi-markers', 'search-result-marker', 'map-click-marker', 'route-lines']
        layers.forEach(layerId => {
          if (map.current?.getLayer(layerId)) {
            map.current.moveLayer('selected-waypoint-marker', layerId)
          }
        })
      }
    } catch (error) {
      if (map.current?.isStyleLoaded()) {
      }
    }
  }, [map, isLoaded, styleLoaded, selectedWaypoint])
}

